import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Brain, Home, FileText, Loader2, Trophy, Zap, CheckCircle2, Lightbulb, TrendingUp, Sparkles, Upload, X, Download, Calculator, Clock, Share2, BarChart3, Save, ChevronRight } from 'lucide-react';
import { trackPageView, trackFeatureUse } from '../utils/analytics';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import SaveResultModal from '../components/SaveResultModal';
import SaveWithEmailModal from '../components/SaveWithEmailModal';
import ScoreBreakdownModal from '../components/ScoreBreakdownModal';
import ShareableResultCard from '../components/ShareableResultCard';
import Tooltip from '../components/Tooltip';
import { useAuth } from '../contexts/AuthContext';
import { useWorkRefresh } from '../contexts/WorkRefreshContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const QuickEvaluate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { refreshAfterEvaluation } = useWorkRefresh();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [paperType, setPaperType] = useState('research');
  const [analyzing, setAnalyzing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState('Initializing AI analysis...');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showScoreBreakdown, setShowScoreBreakdown] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [breakdownScore, setBreakdownScore] = useState(null);
  const [breakdownDimension, setBreakdownDimension] = useState(null);
  const [savedEvaluationId, setSavedEvaluationId] = useState(null);
  const [fromSessionId, setFromSessionId] = useState(null);
  
  // Progressive evaluation state
  const [progressiveResults, setProgressiveResults] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [useProgressiveMode, setUseProgressiveMode] = useState(false);  // Default OFF - Progressive is slow
  
  // Multi-model consensus state - DEFAULT ON for faster, more reliable evaluations
  const [useConsensusMode, setUseConsensusMode] = useState(true);  // Default ON - Consensus is faster & cheaper
  const [consensusData, setConsensusData] = useState(null);
  const [industryMode, setIndustryMode] = useState('general'); // Industry-specific evaluation
  
  // Fast mode state - NEW: Single LLM call for quickest evaluation (~15-30 seconds)
  const [useFastMode, setUseFastMode] = useState(false);  // Default OFF - Use when speed is critical
  
  // Upload progress state
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStartTime, setUploadStartTime] = useState(null);
  const [uploadElapsed, setUploadElapsed] = useState(0);
  
  // Track page view
  useEffect(() => {
    trackPageView('/quick-evaluate');
  }, []);

  // Handle prefilled content from IF Session Report
  useEffect(() => {
    if (location.state?.prefilledContent) {
      setContent(location.state.prefilledContent);
      if (location.state.prefilledTitle) {
        setTitle(location.state.prefilledTitle);
      }
      if (location.state.fromSession) {
        setFromSessionId(location.state.fromSession);
      }
      setPaperType('idea'); // Set paper type to idea for session reports
      
      toast({
        title: "Content Loaded",
        description: "Your idea report has been loaded. Click 'Evaluate My Paper' to analyze it."
      });
    }
  }, [location.state]);

  // Add print styles
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = `
      @media print {
        /* Hide navigation and action buttons */
        header, .print-hide {
          display: none !important;
        }
        
        /* Optimize page breaks */
        .print-section {
          page-break-inside: avoid;
        }
        
        /* Remove background colors for print */
        body {
          background: white !important;
        }
        
        /* Ensure all content is visible */
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        
        /* Make cards print-friendly */
        .card {
          border: 1px solid #ddd !important;
          box-shadow: none !important;
        }
      }
    `;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    const validExtensions = ['.txt', '.pdf', '.docx'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    if (!validExtensions.includes(fileExtension)) {
      toast({
        title: "Invalid File Type",
        description: "Please upload a TXT, PDF, or DOCX file",
        variant: "destructive"
      });
      return;
    }

    // Check file size (25MB limit)
    if (file.size > 25 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Please upload a file smaller than 25MB",
        variant: "destructive"
      });
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Use fast upload endpoint (text extraction only, no AI analysis)
      // For large files (>25MB), increase timeout to 5 minutes
      const timeoutMs = file.size > 10 * 1024 * 1024 ? 300000 : 120000;
      
      const uploadResponse = await axios.post(`${BACKEND_URL}/api/upload-document-fast`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: timeoutMs,
        onUploadProgress: (progressEvent) => {
          // Real upload progress (0-90%)
          const realProgress = Math.round((progressEvent.loaded * 90) / progressEvent.total);
          setUploadProgress(realProgress);
        }
      });

      // Show processing state (90-100%)
      setUploadProgress(95);
      await new Promise(resolve => setTimeout(resolve, 200));
      setUploadProgress(100);

      const extractedTitle = uploadResponse.data.title_suggestion || file.name.replace(/\.[^/.]+$/, '');
      const extractedContent = uploadResponse.data.original_content || '';
      
      setTitle(extractedTitle);
      setContent(extractedContent);
      setUploadedFile(file.name);
      
      toast({
        title: "✅ File Uploaded!",
        description: `${uploadResponse.data.word_count?.toLocaleString() || 'Content'} words extracted. Click 'Evaluate My Paper' to analyze.`,
      });
      
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
      }, 500);
      
    } catch (error) {
      console.error('Upload error:', error);
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' ? errorDetail : 
                          Array.isArray(errorDetail) ? errorDetail[0]?.msg || 'Validation error' :
                          "Failed to process document. Please try again.";
      toast({
        title: "Upload Failed",
        description: errorMessage,
        variant: "destructive"
      });
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setTitle('');
    setContent('');
  };

  // FAST MODE evaluation - single optimized LLM call (~15-30 seconds)
  const handleFastEvaluate = async () => {
    if (!title.trim() || !content.trim()) {
      toast({
        title: "Missing Information",
        description: "Please provide both title and content",
        variant: "destructive"
      });
      return;
    }

    if (content.trim().length < 100) {
      toast({
        title: "Content Too Short",
        description: "Please provide at least 100 characters of content for a proper evaluation",
        variant: "destructive"
      });
      return;
    }

    trackFeatureUse('quick_evaluate_fast', {
      paper_type: paperType,
      has_file: !!uploadedFile
    });

    setAnalyzing(true);
    setElapsedTime(0);
    setProgressiveResults({});
    setResults(null);
    setConsensusData(null);
    setLoadingMessage('⚡ Running fast evaluation (single LLM call)...');

    const messages = [
      '⚡ Running fast evaluation...',
      '🧠 AI is analyzing your paper...',
      '📊 Calculating scores...',
      '✨ Almost done...'
    ];

    let messageIndex = 0;
    const messageInterval = setInterval(() => {
      messageIndex = (messageIndex + 1) % messages.length;
      setLoadingMessage(messages[messageIndex]);
    }, 5000);

    const timerInterval = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/evaluate-paper-fast`, {
        title: title,
        content: content,
        paper_type: paperType
      }, { timeout: 60000 }); // 1 minute timeout

      clearInterval(messageInterval);
      clearInterval(timerInterval);

      if (response.data.success) {
        const evalData = response.data.evaluation;
        
        const formattedResults = {
          paper_summary: evalData.oi?.paper_summary || evalData.paper_summary || '',
          oi: {
            total: evalData.oi?.total || 0,
            novelty: evalData.oi?.novelty || 0,
            resonance: evalData.oi?.resonance || 0,
            synthesis: evalData.oi?.synthesis || 0,
            ethical: evalData.oi?.ethical || 0,
            interpretation: evalData.oi?.interpretation || 'Moderate',
            formula: evalData.oi?.formula || 'OI = (0.3 × Novelty) + (0.3 × Resonance) + (0.2 × Synthesis) + (0.2 × Ethical)',
            novelty_reasoning: evalData.oi?.novelty_reasoning || '',
            resonance_reasoning: evalData.oi?.resonance_reasoning || '',
            synthesis_reasoning: evalData.oi?.synthesis_reasoning || '',
            ethical_reasoning: evalData.oi?.ethical_reasoning || '',
            key_strengths: evalData.oi?.key_strengths || [],
            key_weaknesses: evalData.oi?.key_weaknesses || []
          },
          msa: {
            spark_pct: evalData.msa?.spark_pct || 50,
            mirror_pct: evalData.msa?.mirror_pct || 50,
            assessment: evalData.msa?.assessment || 'Balanced'
          }
        };
        
        setResults(formattedResults);
        
        toast({
          title: "Fast Evaluation Complete!",
          description: `Completed in ${response.data.elapsed_seconds || elapsedTime} seconds`
        });
        
        setTimeout(() => {
          setShowSaveModal(true);
        }, 500);
      } else {
        throw new Error(response.data.error || 'Fast evaluation failed');
      }
    } catch (error) {
      clearInterval(messageInterval);
      clearInterval(timerInterval);
      console.error('Fast evaluation error:', error);
      toast({
        title: "Evaluation Failed",
        description: error.response?.data?.detail || error.message || "Please try again.",
        variant: "destructive"
      });
    } finally {
      setAnalyzing(false);
    }
  };

  // Multi-model consensus evaluation - runs GPT, Claude, and Gemini
  const handleConsensusEvaluate = async () => {
    if (!title.trim() || !content.trim()) {
      toast({
        title: "Missing Information",
        description: "Please provide both title and content",
        variant: "destructive"
      });
      return;
    }

    if (content.trim().length < 100) {
      toast({
        title: "Content Too Short",
        description: "Please provide at least 100 characters of content for a proper evaluation",
        variant: "destructive"
      });
      return;
    }

    trackFeatureUse('quick_evaluate_consensus', {
      paper_type: paperType,
      has_file: !!uploadedFile
    });

    setAnalyzing(true);
    setElapsedTime(0);
    setProgressiveResults({});
    setResults(null);
    setConsensusData(null);
    setLoadingMessage('🤖 Running multi-model consensus (GPT + Claude + Gemini)...');

    const messages = [
      '🤖 Running multi-model consensus...',
      '🧠 GPT-5.1 is analyzing...',
      '💜 Claude Sonnet 4 is evaluating...',
      '🔵 Gemini 2.5 Flash is assessing...',
      '📊 Comparing model outputs...',
      '⚖️ Calculating weighted averages...',
      '🎯 Checking for disagreements...',
      '✨ Finalizing consensus scores...'
    ];

    let messageIndex = 0;
    const messageInterval = setInterval(() => {
      messageIndex = (messageIndex + 1) % messages.length;
      setLoadingMessage(messages[messageIndex]);
    }, 3000);

    const timerInterval = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/evaluate-paper-consensus`, {
        title: title,
        content: content,
        paper_type: paperType,
        industry_mode: industryMode // Industry-specific evaluation
      }, { timeout: 180000 }); // 3 minute timeout for consensus + detailed analysis

      clearInterval(messageInterval);
      clearInterval(timerInterval);

      if (response.data.success) {
        // Store consensus metadata
        setConsensusData(response.data.consensus);
        
        // Format results to match standard evaluation structure with nested oi object
        const evalData = response.data.evaluation;
        
        const formattedResults = {
          paper_summary: evalData.oi?.paper_summary || evalData.paper_summary || '',
          oi: {
            total: evalData.oi?.total || 0,
            novelty: evalData.oi?.novelty || 0,
            resonance: evalData.oi?.resonance || 0,
            synthesis: evalData.oi?.synthesis || 0,
            ethical: evalData.oi?.ethical || 0,
            interpretation: evalData.oi?.interpretation || 'Moderate',
            formula: evalData.oi?.formula || 'OI = (0.3 × Novelty) + (0.3 × Resonance) + (0.2 × Synthesis) + (0.2 × Ethical)',
            novelty_reasoning: evalData.oi?.novelty_reasoning || '',
            resonance_reasoning: evalData.oi?.resonance_reasoning || '',
            synthesis_reasoning: evalData.oi?.synthesis_reasoning || '',
            ethical_reasoning: evalData.oi?.ethical_reasoning || '',
            key_strengths: evalData.oi?.key_strengths || [],
            improvement_areas: evalData.oi?.improvement_areas || [],
            // Detailed per-dimension strengths/weaknesses
            novelty_strengths: evalData.oi?.novelty_strengths || [],
            novelty_weaknesses: evalData.oi?.novelty_weaknesses || [],
            resonance_strengths: evalData.oi?.resonance_strengths || [],
            resonance_weaknesses: evalData.oi?.resonance_weaknesses || [],
            synthesis_strengths: evalData.oi?.synthesis_strengths || [],
            synthesis_weaknesses: evalData.oi?.synthesis_weaknesses || [],
            ethical_strengths: evalData.oi?.ethical_strengths || [],
            ethical_weaknesses: evalData.oi?.ethical_weaknesses || [],
            improvement_advice: evalData.oi?.improvement_advice || [],
            // Geographic analysis
            geographic_analysis: evalData.oi?.geographic_analysis || null
          },
          msa: {
            ...(evalData.msa || { mirror_pct: 50, spark_pct: 50, assessment: 'Balanced' }),
            interpretation: evalData.msa?.interpretation || ''
          },
          // Detailed analysis sections
          uniqueness_analysis: evalData.uniqueness_analysis || null,
          impact_analysis: evalData.impact_analysis || null,
          consensus: response.data.consensus,
          industry_benchmark: response.data.industry_benchmark || null,
          industry_mode: response.data.consensus?.industry_mode || null,
          method: 'multi_model_consensus'
        };
        
        setResults(formattedResults);
        
        // Check for disagreements and notify user
        if (response.data.consensus?.has_disagreement) {
          toast({
            title: "⚠️ Model Disagreement Detected",
            description: `Confidence: ${response.data.consensus.confidence}. ${response.data.consensus.confidence_reason}`,
            variant: "warning",
            duration: 10000
          });
        } else {
          toast({
            title: "✅ High Confidence Consensus",
            description: `${response.data.consensus.models_used} models agree on the evaluation.`,
          });
        }
        
        // Auto-show save modal after results are rendered
        setTimeout(() => {
          if (user) {
            setShowSaveModal(true);
          } else {
            setShowEmailModal(true);
          }
        }, 1500);
      }
    } catch (error) {
      clearInterval(messageInterval);
      clearInterval(timerInterval);
      console.error('Consensus analysis error:', error);
      
      // Auto-fallback to fast evaluation instead of showing error
      toast({
        title: "Switching to Fast Mode",
        description: "Multi-model analysis timed out. Running fast evaluation...",
      });
      
      try {
        setLoadingMessage('Running fast evaluation as fallback...');
        const fastResponse = await axios.post(`${BACKEND_URL}/api/evaluate-paper-fast`, {
          title, content, paper_type: paperType
        }, { timeout: 60000 });
        
        if (fastResponse.data.success) {
          const evalData = fastResponse.data.evaluation;
          const formattedResults = {
            paper_summary: evalData.oi?.paper_summary || evalData.paper_summary || '',
            oi: {
              total: evalData.oi?.total || 0,
              novelty: evalData.oi?.novelty || 0,
              resonance: evalData.oi?.resonance || 0,
              synthesis: evalData.oi?.synthesis || 0,
              ethical: evalData.oi?.ethical || 0,
              interpretation: evalData.oi?.interpretation || 'Moderate',
              formula: evalData.oi?.formula || 'OI = (0.3 × Novelty) + (0.3 × Resonance) + (0.2 × Synthesis) + (0.2 × Ethical)',
              novelty_reasoning: evalData.oi?.novelty_reasoning || '',
              resonance_reasoning: evalData.oi?.resonance_reasoning || '',
              synthesis_reasoning: evalData.oi?.synthesis_reasoning || '',
              ethical_reasoning: evalData.oi?.ethical_reasoning || '',
              key_strengths: evalData.oi?.key_strengths || [],
              key_weaknesses: evalData.oi?.key_weaknesses || []
            },
            msa: {
              spark_pct: evalData.msa?.spark_pct || 50,
              mirror_pct: evalData.msa?.mirror_pct || 50,
              assessment: evalData.msa?.assessment || 'Balanced'
            }
          };
          setResults(formattedResults);
          toast({ title: "Evaluation Complete!", description: `Completed via fast mode in ${fastResponse.data.elapsed_seconds || '~15'}s` });
          setTimeout(() => { if (user) setShowSaveModal(true); else setShowEmailModal(true); }, 1500);
        } else {
          throw new Error('Fast evaluation also failed');
        }
      } catch (fallbackErr) {
        console.error('Fallback fast eval also failed:', fallbackErr);
        toast({
          title: "Evaluation Failed",
          description: "Both consensus and fast modes failed. Please try again in a moment.",
          variant: "destructive"
        });
      }
    } finally {
      setAnalyzing(false);
    }
  };

  // Progressive evaluation - evaluates dimensions one by one
  const handleProgressiveEvaluate = async () => {
    if (!title.trim() || !content.trim()) {
      toast({
        title: "Missing Information",
        description: "Please provide both title and content",
        variant: "destructive"
      });
      return;
    }

    if (content.trim().length < 100) {
      toast({
        title: "Content Too Short",
        description: "Please provide at least 100 characters of content for a proper evaluation",
        variant: "destructive"
      });
      return;
    }

    trackFeatureUse('quick_evaluate_progressive', {
      paper_type: paperType,
      has_file: !!uploadedFile
    });

    setAnalyzing(true);
    setElapsedTime(0);
    setProgressiveResults({});
    setResults(null);
    setCurrentStep(0);

    const timerInterval = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);

    // Evaluation steps in order
    const steps = [
      { dimension: 'summary', message: '📝 Generating paper summary...' },
      { dimension: 'novelty', message: '💡 Evaluating novelty & originality...' },
      { dimension: 'resonance', message: '🎯 Assessing problem-solution fit...' },
      { dimension: 'synthesis', message: '🧬 Examining synthesis quality...' },
      { dimension: 'ethical', message: '⚖️ Reviewing ethical anchoring...' },
      { dimension: 'msa', message: '✨ Calculating Mirror-Spark balance...' },
      { dimension: 'uniqueness', message: '🔮 Analyzing uniqueness...' },
      { dimension: 'impact', message: '🚀 Evaluating potential impact...' }
    ];

    try {
      const accumulated = {};
      
      for (let i = 0; i < steps.length; i++) {
        const step = steps[i];
        setCurrentStep(i + 1);
        setLoadingMessage(step.message);
        
        try {
          const response = await axios.post(`${BACKEND_URL}/api/evaluate-paper-progressive`, {
            title: title,
            content: content,
            paper_type: paperType,
            dimension: step.dimension,
            industry_mode: industryMode  // Pass industry mode for specialized criteria
          });
          
          // Merge result into accumulated
          if (response.data.data) {
            accumulated[step.dimension] = response.data.data;
            
            // Update progressive results to show immediately
            setProgressiveResults(prev => ({
              ...prev,
              [step.dimension]: response.data.data
            }));
          }
        } catch (stepError) {
          console.error(`Error evaluating ${step.dimension}:`, stepError);
          // Continue with other dimensions even if one fails
        }
      }
      
      clearInterval(timerInterval);
      
      // Fetch industry benchmark if industry mode is not general
      if (industryMode && industryMode !== 'general') {
        try {
          setLoadingMessage('📊 Fetching industry benchmark...');
          const benchmarkResponse = await axios.get(`${BACKEND_URL}/api/industry-benchmark/${industryMode}`);
          if (benchmarkResponse.data) {
            // Calculate percentiles based on the current evaluation scores
            const noveltyScore = accumulated.novelty?.novelty || 7;
            const resonanceScore = accumulated.resonance?.resonance || 7;
            const synthesisScore = accumulated.synthesis?.synthesis || 7;
            const ethicalScore = accumulated.ethical?.ethical || 7;
            const totalOI = (0.3 * noveltyScore) + (0.3 * resonanceScore) + (0.2 * synthesisScore) + (0.2 * ethicalScore);
            
            const avgData = benchmarkResponse.data.averages || {};
            
            // Calculate percentiles (simplified - above average = higher percentile)
            const calculatePercentile = (score, avg) => {
              if (!avg) return 50;
              const diff = score - avg;
              // Simple percentile calculation: 50 + (diff * 10), capped at 1-99
              return Math.min(99, Math.max(1, Math.round(50 + (diff * 10))));
            };
            
            // Get interpretation based on percentile
            const getInterpretation = (percentile) => {
              if (percentile >= 75) return 'Above average for this industry';
              if (percentile >= 50) return 'Average for this industry';
              if (percentile >= 25) return 'Below average for this industry';
              return 'Needs improvement';
            };
            
            const noveltyPercentile = calculatePercentile(noveltyScore, avgData.novelty);
            const resonancePercentile = calculatePercentile(resonanceScore, avgData.resonance);
            const synthesisPercentile = calculatePercentile(synthesisScore, avgData.synthesis);
            const ethicalPercentile = calculatePercentile(ethicalScore, avgData.ethical);
            const totalPercentile = calculatePercentile(totalOI, avgData.total_oi);
            
            accumulated.benchmark = {
              benchmark_available: true,
              percentiles: {
                novelty: { percentile: noveltyPercentile, interpretation: getInterpretation(noveltyPercentile) },
                resonance: { percentile: resonancePercentile, interpretation: getInterpretation(resonancePercentile) },
                synthesis: { percentile: synthesisPercentile, interpretation: getInterpretation(synthesisPercentile) },
                ethical: { percentile: ethicalPercentile, interpretation: getInterpretation(ethicalPercentile) },
                total_oi: { percentile: totalPercentile, interpretation: getInterpretation(totalPercentile) }
              },
              overall_ranking: {
                percentile: totalPercentile,
                tier: totalOI >= avgData.total_oi + 1 ? 'Above Average' : 
                      totalOI >= avgData.total_oi - 0.5 ? 'Average' : 'Below Average',
                description: `Your evaluation scores compared to ${benchmarkResponse.data.total_evaluations} other ${industryMode.replace(/_/g, ' ')} evaluations`
              },
              industry_insights: [
                `Industry average OI: ${avgData.total_oi?.toFixed(2) || 'N/A'}`,
                `Your OI: ${totalOI.toFixed(2)}`,
                `Range in industry: ${benchmarkResponse.data.range?.min?.toFixed(1) || 'N/A'} - ${benchmarkResponse.data.range?.max?.toFixed(1) || 'N/A'}`
              ],
              sample_size: benchmarkResponse.data.total_evaluations
            };
          }
        } catch (benchmarkError) {
          console.log('Industry benchmark not available:', benchmarkError.message);
        }
      }
      
      // Combine all results into final format
      const finalResults = buildFinalResults(accumulated);
      setResults(finalResults);
      
      // Show save modal after a short delay to ensure results are rendered
      setTimeout(() => {
        if (user) {
          setShowSaveModal(true);
        } else {
          setShowEmailModal(true);
        }
      }, 1000);
      
    } catch (error) {
      clearInterval(timerInterval);
      console.error('Progressive analysis error:', error);
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' ? errorDetail : 
                          Array.isArray(errorDetail) ? errorDetail[0]?.msg || 'Validation error' :
                          "Please try again.";
      toast({
        title: "Analysis Failed",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setAnalyzing(false);
      setCurrentStep(0);
    }
  };

  // Helper function to build final results from progressive data
  const buildFinalResults = (accumulated) => {
    const novelty = accumulated.novelty?.novelty || 7;
    const resonance = accumulated.resonance?.resonance || 7;
    const synthesis = accumulated.synthesis?.synthesis || 7;
    const ethical = accumulated.ethical?.ethical || 7;
    
    const oiTotal = (0.3 * novelty) + (0.3 * resonance) + (0.2 * synthesis) + (0.2 * ethical);
    
    return {
      paper_summary: accumulated.summary?.paper_summary || '',
      oi: {
        total: Math.round(oiTotal * 100) / 100,
        formula: "OI = (0.3 × Novelty) + (0.3 × Resonance) + (0.2 × Synthesis) + (0.2 × Ethical)",
        interpretation: oiTotal >= 9 ? "Exceptional" : oiTotal >= 8 ? "Very High" : oiTotal >= 6 ? "Moderate" : "Developing",
        novelty: novelty,
        novelty_reasoning: accumulated.novelty?.novelty_reasoning || '',
        novelty_strengths: accumulated.novelty?.novelty_strengths || [],
        novelty_weaknesses: accumulated.novelty?.novelty_weaknesses || [],
        resonance: resonance,
        resonance_reasoning: accumulated.resonance?.resonance_reasoning || '',
        resonance_strengths: accumulated.resonance?.resonance_strengths || [],
        resonance_weaknesses: accumulated.resonance?.resonance_weaknesses || [],
        synthesis: synthesis,
        synthesis_reasoning: accumulated.synthesis?.synthesis_reasoning || '',
        synthesis_strengths: accumulated.synthesis?.synthesis_strengths || [],
        synthesis_weaknesses: accumulated.synthesis?.synthesis_weaknesses || [],
        ethical: ethical,
        ethical_reasoning: accumulated.ethical?.ethical_reasoning || '',
        ethical_strengths: accumulated.ethical?.ethical_strengths || [],
        ethical_weaknesses: accumulated.ethical?.ethical_weaknesses || [],
        // Include geographic_analysis from ethical dimension (E4 feature)
        geographic_analysis: accumulated.ethical?.geographic_analysis || null
      },
      msa: accumulated.msa || {
        mirror_pct: 50,
        spark_pct: 50,
        assessment: "Balanced Innovation",
        interpretation: ""
      },
      uniqueness_analysis: accumulated.uniqueness || {},
      impact_analysis: accumulated.impact || {},
      // Include industry_benchmark if available
      industry_benchmark: accumulated.benchmark || null,
      industry_mode: industryMode
    };
  };

  const handleEvaluate = async () => {
    // FAST MODE - Single LLM call (~15-30 seconds)
    if (useFastMode) {
      return handleFastEvaluate();
    }
    
    // Use consensus mode for high-accuracy evaluation
    if (useConsensusMode) {
      return handleConsensusEvaluate();
    }
    
    // Use progressive mode for better UX
    if (useProgressiveMode) {
      return handleProgressiveEvaluate();
    }
    
    // Original full evaluation (fallback)
    if (!title.trim() || !content.trim()) {
      toast({
        title: "Missing Information",
        description: "Please provide both title and content",
        variant: "destructive"
      });
      return;
    }

    trackFeatureUse('quick_evaluate', {
      paper_type: paperType,
      has_file: !!uploadedFile
    });

    setAnalyzing(true);
    setElapsedTime(0);
    setLoadingMessage('🔍 Analyzing paper structure...');
    
    const messages = [
      '🔍 Analyzing paper structure...',
      '💡 Evaluating novelty and originality...',
      '🎯 Assessing problem-solution fit...',
      '🧬 Examining synthesis quality...',
      '⚖️ Reviewing ethical considerations...',
      '📊 Calculating Originality Index...',
      '✨ Generating detailed insights...',
      '🎨 Finalizing assessment...'
    ];
    
    let messageIndex = 0;
    const messageInterval = setInterval(() => {
      messageIndex = (messageIndex + 1) % messages.length;
      setLoadingMessage(messages[messageIndex]);
    }, 4000);
    
    const timerInterval = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/evaluate-paper`, {
        title: title,
        content: content,
        paper_type: paperType,
        industry_mode: industryMode  // Pass industry mode for specialized criteria
      });

      clearInterval(messageInterval);
      clearInterval(timerInterval);
      setResults(response.data);
      
      setTimeout(() => {
        setShowSaveModal(true);
      }, 500);
    } catch (error) {
      clearInterval(messageInterval);
      clearInterval(timerInterval);
      console.error('Analysis error:', error);
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' ? errorDetail : 
                          Array.isArray(errorDetail) ? errorDetail[0]?.msg || 'Validation error' :
                          "Please try again.";
      toast({
        title: "Analysis Failed",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setAnalyzing(false);
    }
  };

  const handleReset = () => {
    setTitle('');
    setContent('');
    setResults(null);
    setUploadedFile(null);
    setSavedEvaluationId(null);
    setProgressiveResults({});
    setCurrentStep(0);
  };

  const handleScoreClick = (score, dimension = null) => {
    // Ensure score is a valid number
    const numScore = typeof score === 'number' ? score : parseFloat(score) || 0;
    
    setBreakdownScore(numScore);
    setBreakdownDimension(dimension);
    setShowScoreBreakdown(true);
  };

  const handleDownloadPDF = async () => {
    if (!savedEvaluationId) {
      toast({
        title: "Save Required",
        description: "Please save your evaluation first to download the PDF certificate",
        variant: "destructive"
      });
      setShowSaveModal(true);
      return;
    }

    try {
      const pdfUrl = `${BACKEND_URL}/api/evaluations/${savedEvaluationId}/pdf`;
      const token = localStorage.getItem('if_auth_token');
      
      // First try: Open in new tab (most reliable)
      const newWindow = window.open(pdfUrl, '_blank');
      
      if (newWindow) {
        toast({
          title: "PDF Opened!",
          description: "Certificate opened in a new tab. Also sent to your email.",
        });
        return;
      }
      
      // Fallback: Try blob download with auth header
      const response = await fetch(pdfUrl, {
        method: 'GET',
        credentials: 'include',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `IF_Evaluation_${savedEvaluationId}.pdf`;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: "PDF Downloaded!",
        description: "Your certificate has been downloaded and sent to your email",
      });
    } catch (error) {
      console.error('PDF download error:', error);
      toast({
        title: "Download Failed",
        description: "Failed to download PDF. Try right-clicking and 'Open link in new tab'.",
        variant: "destructive"
      });
    }
  };

  const prepareSaveData = () => {
    if (!results) return null;

    // Use AI-generated summary if available, otherwise create brief abstract from content
    const briefAbstract = results.paper_summary || 
      (content.substring(0, 500) + (content.length > 500 ? '...' : ''));

    return {
      paper_title: title,
      paper_abstract: briefAbstract,
      paper_type: paperType,
      oi_total: results.oi?.total || 0,
      oi_novelty: results.oi?.novelty || 0,
      oi_resonance: results.oi?.resonance || 0,
      oi_synthesis: results.oi?.synthesis || 0,
      oi_ethical: results.oi?.ethical || 0,
      oi_interpretation: results.oi?.interpretation || 'Moderate',
      msa_mirror_pct: results.msa?.mirror_pct || 50,
      msa_spark_pct: results.msa?.spark_pct || 50,
      msa_assessment: results.msa?.assessment || 'Balanced',
      full_evaluation: results,
      evaluation_type: 'initial'
    };
  };

  if (uploading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center p-6">
        <Card className="w-full max-w-sm text-center shadow-xl">
          <CardContent className="pt-8 pb-8">
            {/* Circular Progress */}
            <div className="relative inline-block mb-4">
              <div className="w-24 h-24 rounded-full flex items-center justify-center relative">
                <svg className="absolute inset-0 w-24 h-24 transform -rotate-90">
                  <circle cx="48" cy="48" r="42" fill="none" stroke="#e2e8f0" strokeWidth="6" />
                  <circle
                    cx="48" cy="48" r="42"
                    fill="none"
                    stroke="url(#uploadGradient)"
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 42}`}
                    strokeDashoffset={`${2 * Math.PI * 42 * (1 - uploadProgress / 100)}`}
                    className="transition-all duration-300 ease-out"
                  />
                  <defs>
                    <linearGradient id="uploadGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#8b5cf6" />
                      <stop offset="50%" stopColor="#ec4899" />
                      <stop offset="100%" stopColor="#f97316" />
                    </linearGradient>
                  </defs>
                </svg>
                <span className="text-2xl font-bold text-purple-600">{uploadProgress}%</span>
              </div>
            </div>
            
            <h3 className="text-lg font-bold text-slate-900 mb-2">
              {uploadProgress < 90 ? '📤 Uploading...' : 
               uploadProgress < 100 ? '🤖 AI Reading Document...' : '✅ Complete!'}
            </h3>
            
            {/* Progress bar */}
            <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 h-full rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            
            <p className="text-xs text-slate-500 mt-3">
              {uploadProgress < 50 ? 'Uploading file...' : 
               uploadProgress < 90 ? 'Almost there...' :
               uploadProgress < 100 ? 'AI is analyzing your document. This may take 30-60 seconds for large files...' : 'Ready!'}
            </p>
            
            {uploadProgress >= 90 && uploadProgress < 100 && (
              <p className="text-xs text-purple-600 mt-2 animate-pulse">
                ✨ Using AI Vision for best results
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (analyzing) {
    const steps = [
      { key: 'summary', label: 'Paper Summary', icon: '📝' },
      { key: 'novelty', label: 'Novelty', icon: '💡' },
      { key: 'resonance', label: 'Resonance', icon: '🎯' },
      { key: 'synthesis', label: 'Synthesis', icon: '🧬' },
      { key: 'ethical', label: 'Ethical', icon: '⚖️' },
      { key: 'msa', label: 'Mirror-Spark', icon: '✨' },
      { key: 'uniqueness', label: 'Uniqueness', icon: '🔮' },
      { key: 'impact', label: 'Impact', icon: '🚀' }
    ];
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <Card className="mb-6">
            <CardContent className="pt-6 pb-6 text-center">
              <div className="relative inline-block mb-4">
                <Loader2 className="w-16 h-16 text-purple-600 animate-spin" />
                <Brain className="w-8 h-8 text-pink-600 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{loadingMessage}</h3>
              <p className="text-slate-600 text-sm mb-2">Results appear as each dimension completes</p>
              <p className="text-purple-600 text-xs font-medium">📥 Save, Share & Certificate buttons will appear after analysis completes</p>
              <div className="text-lg font-mono text-purple-600">
                {Math.floor(elapsedTime / 60)}:{(elapsedTime % 60).toString().padStart(2, '0')}
              </div>
            </CardContent>
          </Card>

          {/* Progress Steps */}
          <div className="grid grid-cols-4 md:grid-cols-8 gap-2 mb-6">
            {steps.map((step, idx) => (
              <div 
                key={step.key}
                className={`p-2 rounded-lg text-center transition-all ${
                  progressiveResults[step.key] 
                    ? 'bg-green-100 border-2 border-green-400' 
                    : idx + 1 === currentStep 
                      ? 'bg-purple-100 border-2 border-purple-400 animate-pulse' 
                      : 'bg-slate-100 border border-slate-200'
                }`}
              >
                <span className="text-lg">{step.icon}</span>
                <p className="text-xs font-medium mt-1 truncate">{step.label}</p>
                {progressiveResults[step.key] && (
                  <CheckCircle2 className="w-4 h-4 text-green-600 mx-auto mt-1" />
                )}
              </div>
            ))}
          </div>

          {/* Progressive Results Display */}
          <div className="space-y-4">
            {/* Summary */}
            {progressiveResults.summary && (
              <Card className="border-2 border-green-200 bg-green-50/50 animate-in slide-in-from-bottom-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    📝 Paper Summary
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700">{progressiveResults.summary.paper_summary}</p>
                </CardContent>
              </Card>
            )}

            {/* Dimension Scores Row */}
            {(progressiveResults.novelty || progressiveResults.resonance || 
              progressiveResults.synthesis || progressiveResults.ethical) && (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {progressiveResults.novelty && (
                  <Card className="border-2 border-purple-200 bg-purple-50/50 animate-in slide-in-from-bottom-4">
                    <CardContent className="pt-4">
                      <div className="text-center">
                        <span className="text-2xl">💡</span>
                        <p className="text-xs font-semibold text-purple-700 mt-1">Novelty</p>
                        <p className="text-3xl font-bold text-purple-900 mt-2">
                          {progressiveResults.novelty.novelty}/10
                        </p>
                      </div>
                      <p className="text-xs text-slate-600 mt-3 line-clamp-3">
                        {progressiveResults.novelty.novelty_reasoning}
                      </p>
                    </CardContent>
                  </Card>
                )}
                {progressiveResults.resonance && (
                  <Card className="border-2 border-blue-200 bg-blue-50/50 animate-in slide-in-from-bottom-4">
                    <CardContent className="pt-4">
                      <div className="text-center">
                        <span className="text-2xl">🎯</span>
                        <p className="text-xs font-semibold text-blue-700 mt-1">Resonance</p>
                        <p className="text-3xl font-bold text-blue-900 mt-2">
                          {progressiveResults.resonance.resonance}/10
                        </p>
                      </div>
                      <p className="text-xs text-slate-600 mt-3 line-clamp-3">
                        {progressiveResults.resonance.resonance_reasoning}
                      </p>
                    </CardContent>
                  </Card>
                )}
                {progressiveResults.synthesis && (
                  <Card className="border-2 border-green-200 bg-green-50/50 animate-in slide-in-from-bottom-4">
                    <CardContent className="pt-4">
                      <div className="text-center">
                        <span className="text-2xl">🧬</span>
                        <p className="text-xs font-semibold text-green-700 mt-1">Synthesis</p>
                        <p className="text-3xl font-bold text-green-900 mt-2">
                          {progressiveResults.synthesis.synthesis}/10
                        </p>
                      </div>
                      <p className="text-xs text-slate-600 mt-3 line-clamp-3">
                        {progressiveResults.synthesis.synthesis_reasoning}
                      </p>
                    </CardContent>
                  </Card>
                )}
                {progressiveResults.ethical && (
                  <Card className="border-2 border-orange-200 bg-orange-50/50 animate-in slide-in-from-bottom-4">
                    <CardContent className="pt-4">
                      <div className="text-center">
                        <span className="text-2xl">⚖️</span>
                        <p className="text-xs font-semibold text-orange-700 mt-1">Ethical</p>
                        <p className="text-3xl font-bold text-orange-900 mt-2">
                          {progressiveResults.ethical.ethical}/10
                        </p>
                      </div>
                      <p className="text-xs text-slate-600 mt-3 line-clamp-3">
                        {progressiveResults.ethical.ethical_reasoning}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* MSA */}
            {progressiveResults.msa && (
              <Card className="border-2 border-pink-200 bg-pink-50/50 animate-in slide-in-from-bottom-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    ✨ Mirror-Spark Assessment
                    <Badge variant="outline">{progressiveResults.msa.assessment}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4 mb-3">
                    <div className="flex-1">
                      <div className="flex justify-between text-xs mb-1">
                        <span>Mirror (Precedent)</span>
                        <span>{progressiveResults.msa.mirror_pct}%</span>
                      </div>
                      <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-500 transition-all duration-500" 
                          style={{ width: `${progressiveResults.msa.mirror_pct}%` }}
                        />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between text-xs mb-1">
                        <span>Spark (Innovation)</span>
                        <span>{progressiveResults.msa.spark_pct}%</span>
                      </div>
                      <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-orange-500 transition-all duration-500" 
                          style={{ width: `${progressiveResults.msa.spark_pct}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600">{progressiveResults.msa.interpretation}</p>
                </CardContent>
              </Card>
            )}

            {/* Uniqueness & Impact Row */}
            {(progressiveResults.uniqueness || progressiveResults.impact) && (
              <div className="grid md:grid-cols-2 gap-4">
                {progressiveResults.uniqueness && (
                  <Card className="border-2 border-indigo-200 bg-indigo-50/50 animate-in slide-in-from-bottom-4">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">🔮 Uniqueness Analysis</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <p className="text-sm"><strong>Unique Contribution:</strong> {progressiveResults.uniqueness.unique_contribution}</p>
                      {progressiveResults.uniqueness.standout_features?.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-indigo-700 mb-1">Standout Features:</p>
                          <ul className="text-xs text-slate-600 space-y-1">
                            {progressiveResults.uniqueness.standout_features.map((f, i) => (
                              <li key={i}>• {f}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
                {progressiveResults.impact && (
                  <Card className="border-2 border-teal-200 bg-teal-50/50 animate-in slide-in-from-bottom-4">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">🚀 Impact Analysis</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <p className="text-sm"><strong>Potential:</strong> {progressiveResults.impact.potential_impact}</p>
                      <p className="text-sm"><strong>Scale:</strong> {progressiveResults.impact.scale}</p>
                      <p className="text-sm"><strong>Beneficiaries:</strong> {progressiveResults.impact.beneficiaries}</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (results) {
    return (
      <>
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
        {/* Branded Header */}
        <header className="border-b bg-white/90 backdrop-blur-md sticky top-0 z-50 shadow-sm">
          <div className="container mx-auto px-6 py-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 bg-clip-text text-transparent">
                  Idea Fountain
                </h1>
                <p className="text-xs text-slate-500">Evaluation Report</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={handleReset}>
                <FileText className="w-4 h-4 mr-2" />
                New Evaluation
              </Button>
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                <Home className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </div>
          </div>
        </header>

        <div className="container mx-auto px-6 py-8 max-w-6xl">
          {/* Success Banner with Report Actions */}
          <Card className="mb-8 border-0 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 text-white overflow-hidden">
            <CardContent className="pt-8 pb-8 relative">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-32 translate-x-32"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-24 -translate-x-24"></div>
              </div>
              
              <div className="relative z-10">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                  <div className="text-center md:text-left">
                    <div className="flex items-center justify-center md:justify-start gap-3 mb-3">
                      <CheckCircle2 className="w-10 h-10" />
                      <h2 className="text-2xl font-bold">Analysis Complete!</h2>
                    </div>
                    <h3 className="text-lg font-medium opacity-90 mb-1">{title}</h3>
                    <p className="text-sm opacity-80">
                      Evaluated using the Originality Enabling System (OES) Framework
                    </p>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
                    {/* Primary Action: Save & Download */}
                    {!savedEvaluationId ? (
                      <Button 
                        size="lg"
                        onClick={() => {
                          console.log('Save button clicked, user:', user, 'results:', results);
                          if (!user) {
                            setShowEmailModal(true);
                          } else {
                            setShowSaveModal(true);
                          }
                        }}
                        className="bg-white text-purple-700 hover:bg-purple-50 shadow-lg font-semibold"
                      >
                        <Save className="w-5 h-5 mr-2" />
                        Save & Get Certificate
                      </Button>
                    ) : (
                      <Button 
                        size="lg"
                        onClick={handleDownloadPDF}
                        className="bg-white text-purple-700 hover:bg-purple-50 shadow-lg font-semibold"
                      >
                        <Download className="w-5 h-5 mr-2" />
                        Download PDF Certificate
                      </Button>
                    )}
                    
                    {/* Share Button - New Spotify-style sharing */}
                    <Button 
                      size="lg"
                      onClick={() => {
                        if (!savedEvaluationId) {
                          toast({
                            title: "Save First",
                            description: "Please save your evaluation to get a shareable link",
                            variant: "warning"
                          });
                          // Open save modal if logged in, otherwise email modal
                          if (!user) {
                            setShowEmailModal(true);
                          } else {
                            setShowSaveModal(true);
                          }
                        } else {
                          setShowShareModal(true);
                        }
                      }}
                      className="bg-gradient-to-r from-pink-500 to-orange-500 text-white hover:from-pink-600 hover:to-orange-600 shadow-lg font-semibold"
                    >
                      <Share2 className="w-5 h-5 mr-2" />
                      Share Results
                    </Button>
                    
                    <Button 
                      size="lg"
                      variant="outline"
                      onClick={() => window.print()}
                      className="border-2 border-white/50 text-white hover:bg-white/20"
                    >
                      <FileText className="w-5 h-5 mr-2" />
                      Print Report
                    </Button>
                  </div>
                </div>
                
                {/* Certificate ID if saved */}
                {savedEvaluationId && (
                  <div className="mt-4 pt-4 border-t border-white/30">
                    <p className="text-sm opacity-80">
                      📋 Certificate ID: <span className="font-mono font-bold">{savedEvaluationId}</span>
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Paper Summary */}
          {results.paper_summary && (
            <Card className="mb-6 border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg text-blue-900">
                  <FileText className="w-5 h-5" />
                  Paper Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 leading-relaxed">{results.paper_summary}</p>
              </CardContent>
            </Card>
          )}

          {/* OI Score Hero */}
          {results.oi && (
          <Card className="mb-6 border-2 border-purple-200 overflow-hidden">
            <div className="h-2 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500"></div>
            <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-2xl">
                    <Trophy className="w-7 h-7 text-purple-600" />
                    Originality Index: 
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleScoreClick(results.oi.total, null);
                      }}
                      className="text-purple-600 hover:text-purple-800 underline decoration-dotted cursor-pointer inline-flex items-center gap-1"
                      title="Click to see calculation breakdown"
                    >
                      {results.oi.total}/10
                      <Calculator className="w-4 h-4" />
                    </button>
                  </CardTitle>
                  <p className="text-sm text-slate-600 mt-1">{results.oi.formula}</p>
                </div>
                <Badge className={`text-lg px-4 py-2 ${
                  results.oi.total >= 9 ? 'bg-emerald-600' :
                  results.oi.total >= 8 ? 'bg-green-600' :
                  results.oi.total >= 6 ? 'bg-purple-600' :
                  'bg-orange-600'
                } text-white`}>
                  {results.oi.interpretation}
                </Badge>
              </div>
              
              {/* Consensus Info Banner */}
              {results.consensus && (
                <div className={`mt-4 p-4 rounded-lg border-2 ${
                  results.consensus.confidence === 'high' ? 'bg-green-50 border-green-300' :
                  results.consensus.confidence === 'medium' ? 'bg-yellow-50 border-yellow-300' :
                  'bg-red-50 border-red-300'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">
                        {results.consensus.confidence === 'high' ? '🟢' : 
                         results.consensus.confidence === 'medium' ? '🟡' : '🔴'}
                      </span>
                      <div>
                        <p className="font-semibold text-slate-900">
                          Multi-Model Consensus: {results.consensus.confidence.toUpperCase()} Confidence
                        </p>
                        <p className="text-xs text-slate-600">
                          {results.consensus.models_used} models evaluated • {results.consensus.confidence_reason}
                        </p>
                      </div>
                    </div>
                    {results.consensus.has_disagreement && (
                      <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-400">
                        ⚠️ Variance Detected
                      </Badge>
                    )}
                  </div>
                  
                  {/* Model Breakdown Table */}
                  {results.consensus.model_breakdown && (
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <p className="text-xs font-medium text-slate-700 mb-2">Individual Model Scores:</p>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-slate-100">
                              <th className="px-2 py-1 text-left font-medium">Model</th>
                              <th className="px-2 py-1 text-center font-medium">Weight</th>
                              <th className="px-2 py-1 text-center font-medium">Novelty</th>
                              <th className="px-2 py-1 text-center font-medium">Resonance</th>
                              <th className="px-2 py-1 text-center font-medium">Synthesis</th>
                              <th className="px-2 py-1 text-center font-medium">Ethical</th>
                              <th className="px-2 py-1 text-center font-medium">Mirror%</th>
                              <th className="px-2 py-1 text-center font-medium">Spark%</th>
                            </tr>
                          </thead>
                          <tbody>
                            {results.consensus.model_breakdown.map((model, idx) => (
                              <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                <td className="px-2 py-1">
                                  <div>
                                    <span className="font-medium">
                                      {model.status === 'success' ? '✅' : '❌'} {model.model}
                                    </span>
                                    {model.note && (
                                      <p className="text-[10px] text-slate-500 italic">{model.note}</p>
                                    )}
                                  </div>
                                </td>
                                <td className="px-2 py-1 text-center">
                                  <span className={`px-1 py-0.5 rounded ${model.weight === 1.0 ? 'bg-green-100' : 'bg-yellow-100'}`}>
                                    {model.weight}x
                                  </span>
                                </td>
                                <td className="px-2 py-1 text-center">{model.scores?.novelty ?? '-'}</td>
                                <td className="px-2 py-1 text-center">{model.scores?.resonance ?? '-'}</td>
                                <td className="px-2 py-1 text-center">{model.scores?.synthesis ?? '-'}</td>
                                <td className="px-2 py-1 text-center">{model.scores?.ethical ?? '-'}</td>
                                <td className="px-2 py-1 text-center">{model.scores?.mirror_pct ?? '-'}</td>
                                <td className="px-2 py-1 text-center">{model.scores?.spark_pct ?? '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <p className="text-[10px] text-slate-500 mt-1">
                        💡 Weights affect how much each model's score contributes to the consensus average
                      </p>
                    </div>
                  )}
                  
                  {/* Detailed Variance Analysis */}
                  {results.consensus.score_details && Object.keys(results.consensus.score_details).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <p className="text-xs font-medium text-slate-700 mb-2">📊 Variance Analysis:</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {Object.entries(results.consensus.score_details).map(([field, data]) => (
                          <div 
                            key={field} 
                            className={`p-2 rounded text-xs ${
                              data.range > 1.5 ? 'bg-red-100 border border-red-300' : 
                              data.range > 1.0 ? 'bg-yellow-100 border border-yellow-300' : 
                              'bg-green-100 border border-green-300'
                            }`}
                          >
                            <p className="font-semibold capitalize">{field.replace('_', ' ')}</p>
                            <p>Consensus: <span className="font-bold">{data.consensus_score}</span></p>
                            <p>Range: <span className={data.range > 1.5 ? 'text-red-700 font-bold' : ''}>{data.range}</span></p>
                            <p>Std Dev: {data.std_dev}</p>
                            <p className="text-[10px] text-slate-500">
                              Scores: [{data.individual_scores?.join(', ')}]
                            </p>
                          </div>
                        ))}
                      </div>
                      <p className="text-[10px] text-slate-500 mt-2">
                        🟢 Range ≤1.0 = High agreement | 🟡 Range 1.0-1.5 = Moderate | 🔴 Range &gt;1.5 = Significant disagreement
                      </p>
                    </div>
                  )}
                </div>
              )}
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-6">
                {/* Novelty */}
                <div className="p-5 bg-purple-50 rounded-lg border-2 border-purple-200">
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <span className="font-bold text-lg text-purple-900">Novelty (Weight: 30%)</span>
                      <p className="text-xs text-purple-700 mt-1">How unprecedented and unique is this?</p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleScoreClick(results.oi.novelty, 'novelty');
                      }}
                      className="bg-purple-600 text-white text-lg px-4 py-2 rounded-md hover:bg-purple-700 cursor-pointer inline-flex items-center gap-1"
                      title="Click to see how we arrived at this score"
                    >
                      {results.oi.novelty}/10
                      <Calculator className="w-3 h-3" />
                    </button>
                  </div>
                  <p className="text-sm text-slate-700 mb-3">{results.oi.novelty_reasoning}</p>
                  
                  {results.oi.novelty_justification && (
                    <div className="mt-3 p-3 bg-white rounded border border-purple-200">
                      <p className="text-xs font-semibold text-purple-900 mb-1">Detailed Analysis:</p>
                      <p className="text-xs text-slate-700">{results.oi.novelty_justification}</p>
                    </div>
                  )}
                  
                  {(results.oi.novelty_strengths?.length > 0 || results.oi.novelty_weaknesses?.length > 0) && (
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      {results.oi.novelty_strengths?.length > 0 && (
                        <div className="p-2 bg-green-50 rounded border border-green-200">
                          <p className="text-xs font-semibold text-green-900 mb-1">✓ Strengths:</p>
                          <ul className="text-xs text-green-800 space-y-1">
                            {results.oi.novelty_strengths.map((s, i) => (
                              <li key={i}>• {s}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {results.oi.novelty_weaknesses?.length > 0 && (
                        <div className="p-2 bg-red-50 rounded border border-red-200">
                          <p className="text-xs font-semibold text-red-900 mb-1">⚠ Weaknesses:</p>
                          <ul className="text-xs text-red-800 space-y-1">
                            {results.oi.novelty_weaknesses.map((w, i) => (
                              <li key={i}>• {w}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Resonance */}
                <div className="p-5 bg-blue-50 rounded-lg border-2 border-blue-200">
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <span className="font-bold text-lg text-blue-900">Resonance (Weight: 30%)</span>
                      <p className="text-xs text-blue-700 mt-1">How well does it address real needs?</p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleScoreClick(results.oi.resonance, 'resonance');
                      }}
                      className="bg-blue-600 text-white text-lg px-4 py-2 rounded-md hover:bg-blue-700 cursor-pointer inline-flex items-center gap-1"
                      title="Click to see how we arrived at this score"
                    >
                      {results.oi.resonance}/10
                      <Calculator className="w-3 h-3" />
                    </button>
                  </div>
                  <p className="text-sm text-slate-700 mb-3">{results.oi.resonance_reasoning}</p>
                  
                  {results.oi.resonance_justification && (
                    <div className="mt-3 p-3 bg-white rounded border border-blue-200">
                      <p className="text-xs font-semibold text-blue-900 mb-1">Detailed Analysis:</p>
                      <p className="text-xs text-slate-700">{results.oi.resonance_justification}</p>
                    </div>
                  )}
                  
                  {(results.oi.resonance_strengths?.length > 0 || results.oi.resonance_weaknesses?.length > 0) && (
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      {results.oi.resonance_strengths?.length > 0 && (
                        <div className="p-2 bg-green-50 rounded border border-green-200">
                          <p className="text-xs font-semibold text-green-900 mb-1">✓ Strengths:</p>
                          <ul className="text-xs text-green-800 space-y-1">
                            {results.oi.resonance_strengths.map((s, i) => (
                              <li key={i}>• {s}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {results.oi.resonance_weaknesses?.length > 0 && (
                        <div className="p-2 bg-red-50 rounded border border-red-200">
                          <p className="text-xs font-semibold text-red-900 mb-1">⚠ Weaknesses:</p>
                          <ul className="text-xs text-red-800 space-y-1">
                            {results.oi.resonance_weaknesses.map((w, i) => (
                              <li key={i}>• {w}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Synthesis */}
                <div className="p-5 bg-green-50 rounded-lg border-2 border-green-200">
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <span className="font-bold text-lg text-green-900">Synthesis (Weight: 20%)</span>
                      <p className="text-xs text-green-700 mt-1">How effectively does it combine concepts?</p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleScoreClick(results.oi.synthesis, 'synthesis');
                      }}
                      className="bg-green-600 text-white text-lg px-4 py-2 rounded-md hover:bg-green-700 cursor-pointer inline-flex items-center gap-1"
                      title="Click to see how we arrived at this score"
                    >
                      {results.oi.synthesis}/10
                      <Calculator className="w-3 h-3" />
                    </button>
                  </div>
                  <p className="text-sm text-slate-700 mb-3">{results.oi.synthesis_reasoning}</p>
                  
                  {results.oi.synthesis_justification && (
                    <div className="mt-3 p-3 bg-white rounded border border-green-200">
                      <p className="text-xs font-semibold text-green-900 mb-1">Detailed Analysis:</p>
                      <p className="text-xs text-slate-700">{results.oi.synthesis_justification}</p>
                    </div>
                  )}
                  
                  {(results.oi.synthesis_strengths?.length > 0 || results.oi.synthesis_weaknesses?.length > 0) && (
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      {results.oi.synthesis_strengths?.length > 0 && (
                        <div className="p-2 bg-green-50 rounded border border-green-200">
                          <p className="text-xs font-semibold text-green-900 mb-1">✓ Strengths:</p>
                          <ul className="text-xs text-green-800 space-y-1">
                            {results.oi.synthesis_strengths.map((s, i) => (
                              <li key={i}>• {s}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {results.oi.synthesis_weaknesses?.length > 0 && (
                        <div className="p-2 bg-red-50 rounded border border-red-200">
                          <p className="text-xs font-semibold text-red-900 mb-1">⚠ Weaknesses:</p>
                          <ul className="text-xs text-red-800 space-y-1">
                            {results.oi.synthesis_weaknesses.map((w, i) => (
                              <li key={i}>• {w}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Ethical */}
                <div className="p-5 bg-orange-50 rounded-lg border-2 border-orange-200">
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <span className="font-bold text-lg text-orange-900">Ethical Anchoring (Weight: 20%)</span>
                      <p className="text-xs text-orange-700 mt-1">How responsible and inclusive is it?</p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleScoreClick(results.oi.ethical, 'ethical');
                      }}
                      className="bg-orange-600 text-white text-lg px-4 py-2 rounded-md hover:bg-orange-700 cursor-pointer inline-flex items-center gap-1"
                      title="Click to see how we arrived at this score"
                    >
                      {results.oi.ethical}/10
                      <Calculator className="w-3 h-3" />
                    </button>
                  </div>
                  <p className="text-sm text-slate-700 mb-3">{results.oi.ethical_reasoning}</p>
                  
                  {results.oi.ethical_justification && (
                    <div className="mt-3 p-3 bg-white rounded border border-orange-200">
                      <p className="text-xs font-semibold text-orange-900 mb-1">Detailed Analysis:</p>
                      <p className="text-xs text-slate-700">{results.oi.ethical_justification}</p>
                    </div>
                  )}
                  
                  {(results.oi.ethical_strengths?.length > 0 || results.oi.ethical_weaknesses?.length > 0) && (
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      {results.oi.ethical_strengths?.length > 0 && (
                        <div className="p-2 bg-green-50 rounded border border-green-200">
                          <p className="text-xs font-semibold text-green-900 mb-1">✓ Strengths:</p>
                          <ul className="text-xs text-green-800 space-y-1">
                            {results.oi.ethical_strengths.map((s, i) => (
                              <li key={i}>• {s}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {results.oi.ethical_weaknesses?.length > 0 && (
                        <div className="p-2 bg-red-50 rounded border border-red-200">
                          <p className="text-xs font-semibold text-red-900 mb-1">⚠ Weaknesses:</p>
                          <ul className="text-xs text-red-800 space-y-1">
                            {results.oi.ethical_weaknesses.map((w, i) => (
                              <li key={i}>• {w}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Geographic Analysis Section (E4) */}
                  {results.oi.geographic_analysis && (
                    <div className="mt-4 p-4 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg border-2 border-cyan-300">
                      <h4 className="font-bold text-cyan-900 mb-3 flex items-center gap-2">
                        <span>🌍</span>
                        Geographic & Cross-Border Analysis (E4)
                      </h4>
                      
                      {/* Regional Novelty */}
                      <div className="mb-4 p-3 bg-white rounded-lg border border-cyan-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-sm text-cyan-800">Regional Novelty Level:</span>
                          <Badge className={`${
                            results.oi.geographic_analysis.regional_novelty_level === 'globally_novel' ? 'bg-green-600' :
                            results.oi.geographic_analysis.regional_novelty_level === 'regionally_novel' ? 'bg-blue-600' :
                            'bg-amber-600'
                          } text-white text-xs px-3 py-1`}>
                            {results.oi.geographic_analysis.regional_novelty_level?.replace(/_/g, ' ').toUpperCase()}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-700">{results.oi.geographic_analysis.regional_novelty_explanation}</p>
                      </div>

                      {/* Prior Implementations */}
                      {results.oi.geographic_analysis.prior_implementations?.length > 0 && (
                        <div className="mb-4">
                          <h5 className="font-semibold text-xs text-cyan-800 mb-2">📍 Prior Implementations:</h5>
                          <div className="space-y-2">
                            {results.oi.geographic_analysis.prior_implementations.map((impl, idx) => (
                              <div key={idx} className="p-2 bg-white rounded border border-cyan-100 text-xs">
                                <span className="font-semibold text-cyan-900">{impl.country_region}</span>
                                {impl.year && impl.year !== 'Unknown' && <span className="text-slate-500"> ({impl.year})</span>}
                                <p className="text-slate-600 mt-1">{impl.implementation}</p>
                                <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs ${
                                  impl.outcome?.toLowerCase().includes('success') ? 'bg-green-100 text-green-800' :
                                  impl.outcome?.toLowerCase().includes('fail') ? 'bg-red-100 text-red-800' :
                                  'bg-amber-100 text-amber-800'
                                }`}>
                                  {impl.outcome}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Transferability */}
                      {results.oi.geographic_analysis.transferability_score && (
                        <div className="mb-4 p-3 bg-white rounded-lg border border-cyan-200">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-sm text-cyan-800">Transferability Score:</span>
                            <span className="text-lg font-bold text-cyan-900">{results.oi.geographic_analysis.transferability_score}/10</span>
                          </div>
                          {results.oi.geographic_analysis.transferability_factors && (
                            <div className="grid grid-cols-2 gap-2 mt-2">
                              {Object.entries(results.oi.geographic_analysis.transferability_factors).map(([key, value]) => (
                                <div key={key} className="flex justify-between text-xs">
                                  <span className="text-slate-600">{key.replace(/_/g, ' ')}:</span>
                                  <Badge className={`${
                                    value === 'high' ? 'bg-green-100 text-green-800' :
                                    value === 'medium' ? 'bg-amber-100 text-amber-800' :
                                    'bg-red-100 text-red-800'
                                  } text-xs px-2 py-0.5`}>
                                    {value}
                                  </Badge>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Recommendations */}
                      <div className="grid grid-cols-2 gap-3">
                        {results.oi.geographic_analysis.recommended_regions_for_adoption?.length > 0 && (
                          <div className="p-2 bg-green-50 rounded border border-green-200">
                            <p className="text-xs font-semibold text-green-900 mb-1">✓ Recommended Regions:</p>
                            <ul className="text-xs text-green-800 space-y-1">
                              {results.oi.geographic_analysis.recommended_regions_for_adoption.map((r, i) => (
                                <li key={i}>• {r}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {results.oi.geographic_analysis.regions_with_barriers?.length > 0 && (
                          <div className="p-2 bg-red-50 rounded border border-red-200">
                            <p className="text-xs font-semibold text-red-900 mb-1">⚠ Regions with Barriers:</p>
                            <ul className="text-xs text-red-800 space-y-1">
                              {results.oi.geographic_analysis.regions_with_barriers.map((r, i) => (
                                <li key={i}>• {r}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {results.oi.improvement_advice?.length > 0 && (
                <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Suggestions for Improvement
                  </h4>
                  <ul className="space-y-2">
                    {results.oi.improvement_advice.map((advice, idx) => (
                      <li key={idx} className="text-sm text-amber-800">✓ {advice}</li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
          )}

          {/* Industry Benchmark */}
          {results.industry_benchmark?.benchmark_available && (
            <Card className="mb-6 border-2 border-indigo-200 print-section">
              <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50">
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <BarChart3 className="w-7 h-7 text-indigo-600" />
                  Industry Benchmark
                  {results.industry_mode && results.industry_mode !== 'general' && (
                    <Badge className="bg-indigo-600 text-white text-sm px-3 py-1 ml-2">
                      {results.industry_mode.replace(/_/g, ' ').toUpperCase()}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {/* Overall Ranking */}
                {results.industry_benchmark.overall_ranking && (
                  <div className="mb-6 p-4 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-lg border border-indigo-200 text-center">
                    <div className="text-4xl font-bold text-indigo-900 mb-1">
                      {results.industry_benchmark.overall_ranking.tier}
                    </div>
                    <div className="text-2xl font-semibold text-indigo-700">
                      Top {100 - results.industry_benchmark.overall_ranking.percentile}%
                    </div>
                    <p className="text-sm text-indigo-600 mt-2">
                      {results.industry_benchmark.overall_ranking.description}
                    </p>
                    {results.industry_benchmark.is_estimated && (
                      <p className="text-xs text-amber-600 mt-2 flex items-center justify-center gap-1">
                        <span>⚠️</span> {results.industry_benchmark.note}
                      </p>
                    )}
                  </div>
                )}

                {/* Percentile Breakdown */}
                {results.industry_benchmark.percentiles && (
                  <div className="grid md:grid-cols-2 gap-4 mb-6">
                    {Object.entries(results.industry_benchmark.percentiles)
                      .filter(([key]) => key !== 'total_oi')
                      .map(([dimension, data]) => (
                        <div key={dimension} className="p-3 bg-white rounded-lg border border-slate-200">
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-semibold text-sm text-slate-700 capitalize">
                              {dimension.replace(/_/g, ' ')}
                            </span>
                            <span className="text-lg font-bold text-indigo-600">
                              {data.percentile !== null ? `${data.percentile}%` : 'N/A'}
                            </span>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${
                                (data.percentile || 0) >= 75 ? 'bg-green-500' :
                                (data.percentile || 0) >= 50 ? 'bg-blue-500' :
                                (data.percentile || 0) >= 25 ? 'bg-amber-500' :
                                'bg-red-400'
                              }`}
                              style={{ width: `${data.percentile || 0}%` }}
                            />
                          </div>
                          <p className="text-xs text-slate-500 mt-1">{data.interpretation}</p>
                        </div>
                      ))}
                  </div>
                )}

                {/* Industry Insights */}
                {results.industry_benchmark.industry_insights?.length > 0 && (
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <h4 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
                      <Lightbulb className="w-4 h-4" />
                      Industry Insights
                    </h4>
                    <ul className="space-y-2">
                      {results.industry_benchmark.industry_insights.map((insight, idx) => (
                        <li key={idx} className="text-sm text-purple-800">{insight}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Sample Size Note */}
                <p className="text-xs text-slate-500 mt-4 text-center">
                  Based on {results.industry_benchmark.sample_size || 0} evaluations in the {results.industry_mode?.replace(/_/g, ' ') || 'general'} industry
                </p>
              </CardContent>
            </Card>
          )}

          {/* MSA Score */}
          {results.msa && (
          <Card className="mb-6 border-2 border-emerald-200 print-section">
            <CardHeader className="bg-gradient-to-r from-emerald-50 to-cyan-50">
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Zap className="w-7 h-7 text-emerald-600" />
                Mirror-Spark Assessment
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="mb-4">
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-semibold text-blue-600">Mirror: {results.msa.mirror_pct}%</span>
                  <Badge className="bg-emerald-600 text-white text-lg px-4 py-2">
                    {results.msa.assessment}
                  </Badge>
                  <span className="text-sm font-semibold text-emerald-600">Spark: {results.msa.spark_pct}%</span>
                </div>
                <div className="h-8 bg-slate-200 rounded-full overflow-hidden flex">
                  <div 
                    className="bg-blue-500 flex items-center justify-center text-white text-xs font-semibold"
                    style={{ width: `${results.msa.mirror_pct}%` }}
                  >
                    {results.msa.mirror_pct > 15 && 'Conventional'}
                  </div>
                  <div 
                    className="bg-emerald-500 flex items-center justify-center text-white text-xs font-semibold"
                    style={{ width: `${results.msa.spark_pct}%` }}
                  >
                    {results.msa.spark_pct > 15 && 'Original'}
                  </div>
                </div>
              </div>

              {results.msa.interpretation && (
                <div className="p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                  <h4 className="font-semibold text-cyan-900 mb-2">What This Means</h4>
                  <p className="text-sm text-cyan-800">{results.msa.interpretation}</p>
                </div>
              )}
            </CardContent>
          </Card>
          )}

          {/* Uniqueness Analysis */}
          {results.uniqueness_analysis && (
            <Card className="mb-6 border-2 border-purple-200 print-section">
              <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50">
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <Sparkles className="w-7 h-7 text-purple-600" />
                  Uniqueness Analysis
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <h4 className="font-semibold text-purple-900 mb-2">Unique Contribution</h4>
                  <p className="text-slate-700">{results.uniqueness_analysis.unique_contribution}</p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                    <h4 className="font-semibold text-indigo-900 mb-2">Novel Angle</h4>
                    <p className="text-sm text-slate-700">{results.uniqueness_analysis.novel_angle}</p>
                  </div>

                  <div className="p-4 bg-violet-50 rounded-lg border border-violet-200">
                    <h4 className="font-semibold text-violet-900 mb-2">Differentiation</h4>
                    <p className="text-sm text-slate-700">{results.uniqueness_analysis.differentiation}</p>
                  </div>
                </div>

                {results.uniqueness_analysis.standout_features?.length > 0 && (
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <h4 className="font-semibold text-purple-900 mb-2">Standout Features</h4>
                    <ul className="space-y-1">
                      {results.uniqueness_analysis.standout_features.map((feature, idx) => (
                        <li key={idx} className="text-sm text-slate-700 flex items-start gap-2">
                          <Sparkles className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Impact Analysis */}
          {results.impact_analysis && (
            <Card className="mb-6 border-2 border-blue-200 print-section">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50">
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <TrendingUp className="w-7 h-7 text-blue-600" />
                  Impact Analysis
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-900 mb-2">Potential Impact</h4>
                  <p className="text-slate-700">{results.impact_analysis.potential_impact}</p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                    <h4 className="font-semibold text-cyan-900 mb-2">Beneficiaries</h4>
                    <p className="text-sm text-slate-700">{results.impact_analysis.beneficiaries}</p>
                  </div>

                  <div className="p-4 bg-sky-50 rounded-lg border border-sky-200">
                    <h4 className="font-semibold text-sky-900 mb-2">Scale</h4>
                    <p className="text-sm text-slate-700">{results.impact_analysis.scale}</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-900 mb-2">Short-term Impact</h4>
                    <p className="text-sm text-slate-700">{results.impact_analysis.short_term_impact}</p>
                  </div>

                  <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                    <h4 className="font-semibold text-indigo-900 mb-2">Long-term Impact</h4>
                    <p className="text-sm text-slate-700">{results.impact_analysis.long_term_impact}</p>
                  </div>
                </div>

                <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border-2 border-blue-300">
                  <h4 className="font-semibold text-blue-900 mb-2">Transformative Potential</h4>
                  <p className="text-slate-700">{results.impact_analysis.transformative_potential}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Action Buttons */}
          <Card className="mb-6 border-2 border-slate-200 bg-gradient-to-r from-slate-50 to-purple-50">
            <CardContent className="pt-6 pb-6">
              <h4 className="font-bold text-slate-900 mb-4 text-center">What would you like to do next?</h4>
              <div className="grid md:grid-cols-5 gap-4">
                <Tooltip content="Use OES framework to transform your paper">
                  <Button
                    className="w-full bg-gradient-to-r from-emerald-600 via-cyan-600 to-blue-600 text-white h-auto py-4"
                    onClick={() => navigate('/new-session', { state: { 
                      fromEvaluation: true, 
                      paperContent: content,
                      paperTitle: title 
                    }})}
                  >
                    <div className="flex flex-col items-center">
                      <Brain className="w-6 h-6 mb-2" />
                      <span className="font-semibold">Transform with OES</span>
                      <span className="text-xs opacity-80">Improve your paper</span>
                    </div>
                  </Button>
                </Tooltip>
                
                <Tooltip content={savedEvaluationId ? "Download your IF certified PDF certificate" : "Save first to get certificate"}>
                  <Button
                    className={`w-full h-auto py-4 ${savedEvaluationId 
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white' 
                      : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'}`}
                    onClick={savedEvaluationId ? handleDownloadPDF : () => {
                      if (!user) {
                        setShowEmailModal(true);
                      } else {
                        setShowSaveModal(true);
                      }
                    }}
                  >
                    <div className="flex flex-col items-center">
                      <Download className="w-6 h-6 mb-2" />
                      <span className="font-semibold">{savedEvaluationId ? 'Download PDF' : 'Save & Get PDF'}</span>
                      <span className="text-xs opacity-80">IF Certified Certificate</span>
                    </div>
                  </Button>
                </Tooltip>
                
                {/* NEW: Share Results Button */}
                <Tooltip content={savedEvaluationId ? "Share your results on social media or with peers" : "Save first to get shareable link"}>
                  <Button
                    className="w-full bg-gradient-to-r from-pink-500 to-orange-500 text-white h-auto py-4"
                    onClick={() => {
                      if (!savedEvaluationId) {
                        toast({
                          title: "Save First",
                          description: "Please save your evaluation to get a shareable link"
                        });
                        if (!user) {
                          setShowEmailModal(true);
                        } else {
                          setShowSaveModal(true);
                        }
                      } else {
                        setShowShareModal(true);
                      }
                    }}
                  >
                    <div className="flex flex-col items-center">
                      <Share2 className="w-6 h-6 mb-2" />
                      <span className="font-semibold">Share Results</span>
                      <span className="text-xs opacity-80">{savedEvaluationId ? "Social & WhatsApp" : "Save first"}</span>
                    </div>
                  </Button>
                </Tooltip>
                
                <Tooltip content="Evaluate another paper">
                  <Button
                    className="w-full bg-gradient-to-r from-orange-600 to-red-600 text-white h-auto py-4"
                    onClick={handleReset}
                  >
                    <div className="flex flex-col items-center">
                      <FileText className="w-6 h-6 mb-2" />
                      <span className="font-semibold">Evaluate Another</span>
                      <span className="text-xs opacity-80">New analysis</span>
                    </div>
                  </Button>
                </Tooltip>
                
                <Tooltip content="Print this evaluation report">
                  <Button
                    variant="outline"
                    className="w-full h-auto py-4 border-2"
                    onClick={() => window.print()}
                  >
                    <div className="flex flex-col items-center">
                      <FileText className="w-6 h-6 mb-2 text-slate-600" />
                      <span className="font-semibold text-slate-700">Print Report</span>
                      <span className="text-xs text-slate-500">Physical copy</span>
                    </div>
                  </Button>
                </Tooltip>
              </div>
            </CardContent>
          </Card>

          {/* Smart Feedback Based on Score */}
          {results.oi.total < 7 && (
            <Card className="mt-6 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-orange-300">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-orange-200 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-6 h-6 text-orange-700" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-orange-900 mb-2 text-lg">
                      {results.oi.total < 5 
                        ? "Your Paper Needs Significant Originality Enhancement" 
                        : "Your Paper Could Be More Original"}
                    </h4>
                    
                    {results.oi.total < 5 ? (
                      <div className="space-y-3">
                        <p className="text-orange-800 font-medium">
                          Your OI score of {results.oi.total}/10 suggests this work may be too derivative. Here's what's holding it back:
                        </p>
                        <ul className="space-y-2 text-sm text-orange-800">
                          {results.oi.novelty < 5 && (
                            <li className="flex items-start gap-2">
                              <span className="text-red-600 font-bold">⚠️</span>
                              <span><strong>Low Novelty ({results.oi.novelty}/10):</strong> Your approach closely follows existing solutions. You need more unique angles.</span>
                            </li>
                          )}
                          {results.oi.resonance < 5 && (
                            <li className="flex items-start gap-2">
                              <span className="text-red-600 font-bold">⚠️</span>
                              <span><strong>Weak Resonance ({results.oi.resonance}/10):</strong> The problem-solution fit isn't compelling. Strengthen the need for this work.</span>
                            </li>
                          )}
                          {results.oi.synthesis < 5 && (
                            <li className="flex items-start gap-2">
                              <span className="text-red-600 font-bold">⚠️</span>
                              <span><strong>Limited Synthesis ({results.oi.synthesis}/10):</strong> Not enough cross-disciplinary integration. Combine more concepts.</span>
                            </li>
                          )}
                          {results.oi.ethical < 5 && (
                            <li className="flex items-start gap-2">
                              <span className="text-red-600 font-bold">⚠️</span>
                              <span><strong>Ethical Concerns ({results.oi.ethical}/10):</strong> Insufficient consideration of impacts and stakeholders.</span>
                            </li>
                          )}
                        </ul>
                        <div className="mt-4 p-4 bg-white rounded-lg border-2 border-orange-300">
                          <p className="text-orange-900 font-semibold mb-3">
                            🚀 <strong>Recommendation:</strong> Use our Originality Enabling System (OES) to transform this work
                          </p>
                          <p className="text-sm text-orange-800 mb-4">
                            Our guided thinking process will help you:
                            • Discover novel angles through Spark Activation
                            • Validate and strengthen through Mirror Analysis
                            • Synthesize concepts from multiple disciplines
                            • Consider ethical dimensions deeply
                          </p>
                          <Button
                            className="w-full bg-gradient-to-r from-orange-600 to-red-600 text-white"
                            size="lg"
                            onClick={() => navigate('/session/new')}
                          >
                            <Brain className="w-5 h-5 mr-2" />
                            Transform This Paper with OES Engine
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-orange-800">
                          Your OI score of {results.oi.total}/10 is moderate, but you're capable of more. Professional papers typically score 7+.
                        </p>
                        <p className="text-sm text-orange-800">
                          <strong>What's limiting you:</strong>
                        </p>
                        <ul className="space-y-1 text-sm text-orange-800 ml-4">
                          {results.oi.novelty < 7 && <li>• Novelty could be stronger - explore more unique approaches</li>}
                          {results.oi.resonance < 7 && <li>• Resonance needs work - strengthen the problem-solution fit</li>}
                          {results.oi.synthesis < 7 && <li>• Synthesis is limited - integrate more diverse concepts</li>}
                          {results.oi.ethical < 7 && <li>• Ethical depth could improve - consider broader impacts</li>}
                        </ul>
                        <div className="mt-4 p-4 bg-white rounded-lg border border-orange-200">
                          <p className="text-orange-900 font-semibold mb-2">
                            💡 Use OES to Break Through to 7+
                          </p>
                          <p className="text-sm text-orange-800 mb-3">
                            Our 3-stage guided process helps you discover hidden angles, validate rigorously, and synthesize brilliantly.
                          </p>
                          <Button
                            className="w-full bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600"
                            onClick={() => navigate('/session/new')}
                          >
                            <Sparkles className="w-4 h-4 mr-2" />
                            Enhance Originality with OES
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Positive reinforcement for high scores */}
          {results.oi.total >= 7 && results.oi.total < 8.5 && (
            <Card className="mt-6 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="w-10 h-10 text-green-600 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="font-bold text-green-900 mb-2">Strong Originality! 🎉</h4>
                    <p className="text-green-800 mb-3">
                      Your OI score of {results.oi.total}/10 shows solid originality. This is publication-worthy!
                    </p>
                    <p className="text-sm text-green-700">
                      Want to push it to exceptional (8.5+)? Our OES thinking process can help you reach the top tier through deeper synthesis and novel connections.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {results.oi.total >= 8.5 && (
            <Card className="mt-6 bg-gradient-to-r from-purple-50 via-pink-50 to-yellow-50 border-2 border-purple-400">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <Trophy className="w-10 h-10 text-purple-600 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="font-bold text-purple-900 mb-2">Exceptional Originality! 🏆</h4>
                    <p className="text-purple-800 mb-2">
                      Your OI score of {results.oi.total}/10 places you in the top tier. This demonstrates exceptional creative thinking!
                    </p>
                    <p className="text-sm text-purple-700">
                      Consider submitting this to top-tier journals or challenges. You might also enjoy exploring new ideas with our full OES engine.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tips */}
          <Card className="mt-6 bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Lightbulb className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-blue-900 mb-2">Using These Results</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Share this assessment with your teacher/advisor to show originality</li>
                    <li>• Use improvement suggestions to strengthen your work before submission</li>
                    <li>• A score of 7+ indicates strong academic originality</li>
                    <li>• Consider the MSA balance - high Spark shows innovative thinking</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modals */}
      <SaveWithEmailModal
        isOpen={showEmailModal && results !== null}
        onClose={() => setShowEmailModal(false)}
        onSuccess={() => { setShowEmailModal(false); setShowSaveModal(true); toast({ title: "Welcome!", description: "Now saving your evaluation..." }); }}
        title="Save Your Evaluation"
        description="Enter your email to save results and get your certificate"
      />
      <SaveResultModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        evaluationData={prepareSaveData()}
        onSaveSuccess={(data) => {
          const evalId = data?.evaluation_id || data?.id;
          if (evalId) {
            setSavedEvaluationId(evalId);
            refreshAfterEvaluation();
            toast({ title: "Evaluation Saved!", description: "You can now download your PDF certificate" });
          } else {
            toast({ title: "Save Completed", description: "Evaluation saved but certificate ID not returned.", variant: "warning" });
          }
        }}
      />
      <ScoreBreakdownModal
        isOpen={showScoreBreakdown}
        onClose={() => setShowScoreBreakdown(false)}
        score={breakdownScore}
        dimension={breakdownDimension}
        fullScores={breakdownDimension === null && results ? { novelty: results.oi.novelty, resonance: results.oi.resonance, synthesis: results.oi.synthesis, ethical: results.oi.ethical } : null}
      />
      <ShareableResultCard
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        evaluationData={results ? { paper_title: title, oi_total: results.oi.total, oi_novelty: results.oi.novelty, oi_resonance: results.oi.resonance, oi_synthesis: results.oi.synthesis, oi_ethical: results.oi.ethical, msa_mirror_pct: results.msa.mirror_pct, msa_spark_pct: results.msa.spark_pct, msa_assessment: results.msa.assessment } : null}
        evaluationId={savedEvaluationId}
        style="vibrant"
      />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Clean Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Evaluate Paper</h1>
          <p className="text-sm text-slate-500 mt-1">Get an instant originality assessment with AI-powered analysis</p>
        </div>

        <div className="space-y-5">
          {/* Upload / Drop Zone */}
          <Card className="border border-slate-200 shadow-sm" data-testid="upload-card">
            <CardContent className="p-6">
              {uploadedFile ? (
                <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    <span className="text-sm font-medium text-slate-700">{uploadedFile}</span>
                  </div>
                  <button onClick={handleRemoveFile} className="text-slate-400 hover:text-red-500 transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label htmlFor="paper-upload" className="cursor-pointer block">
                  <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center hover:border-violet-400 hover:bg-violet-50/30 transition-all">
                    <Upload className="w-8 h-8 mx-auto text-slate-400 mb-3" />
                    <p className="text-sm font-medium text-slate-700">Drop your paper here or click to upload</p>
                    <p className="text-xs text-slate-400 mt-1">PDF, DOCX, TXT (max 25MB) — or paste text below</p>
                  </div>
                </label>
              )}
              <input
                id="paper-upload"
                type="file"
                accept=".pdf,.docx,.txt"
                onChange={handleFileUpload}
                className="hidden"
                disabled={uploading || analyzing}
              />
            </CardContent>
          </Card>

          {/* Title + Content */}
          <Card className="border border-slate-200 shadow-sm" data-testid="content-card">
            <CardContent className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Title</label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Sustainable Urban Farming Using AI-Powered Hydroponics"
                  className="text-base"
                  data-testid="paper-title-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Paper Content</label>
                <Textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Paste your abstract, full paper, or project description here..."
                  className="min-h-[200px] text-sm"
                  data-testid="paper-content-input"
                />
                <p className="text-xs text-slate-400 mt-1.5">
                  {content.length} characters {content.length > 0 && content.length < 100 && '— need at least 100'}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Advanced Settings (Collapsible) */}
          <details className="group" data-testid="advanced-settings">
            <summary className="flex items-center gap-2 cursor-pointer text-sm text-slate-500 hover:text-slate-700 transition-colors select-none">
              <ChevronRight className="w-4 h-4 group-open:rotate-90 transition-transform" />
              Advanced Settings
            </summary>
            <Card className="mt-3 border border-slate-200 shadow-sm">
              <CardContent className="p-5 space-y-4">
                {/* Paper Type */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Paper Type</label>
                  <select
                    value={paperType}
                    onChange={(e) => setPaperType(e.target.value)}
                    className="w-full p-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                  >
                    <option value="research">Research Paper</option>
                    <option value="concept">Concept Paper</option>
                    <option value="industry">Industry Proposal</option>
                    <option value="thesis">Thesis / Dissertation</option>
                  </select>
                </div>

                {/* Industry */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Industry</label>
                  <select
                    value={industryMode}
                    onChange={(e) => setIndustryMode(e.target.value)}
                    className="w-full p-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                  >
                    <option value="general">General (All Industries)</option>
                    <optgroup label="Healthcare & Life Sciences">
                      <option value="healthcare">Healthcare & Medical Research</option>
                      <option value="pharmaceutical">Pharmaceutical & Drug Development</option>
                      <option value="biotechnology">Biotechnology & Life Sciences</option>
                    </optgroup>
                    <optgroup label="Technology & Computing">
                      <option value="technology">Technology & Computer Science</option>
                      <option value="ai_ml">AI & Machine Learning</option>
                      <option value="cybersecurity">Cybersecurity</option>
                    </optgroup>
                    <optgroup label="Finance & Banking">
                      <option value="finance">Finance & Economics</option>
                      <option value="banking">Banking & Financial Services</option>
                      <option value="insurance">Insurance & Actuarial</option>
                    </optgroup>
                    <optgroup label="Engineering & Manufacturing">
                      <option value="engineering">Engineering</option>
                      <option value="manufacturing">Manufacturing & Industrial</option>
                      <option value="robotics">Robotics & Automation</option>
                      <option value="aerospace">Aerospace & Defense</option>
                      <option value="civil_construction">Civil Engineering & Construction</option>
                    </optgroup>
                    <optgroup label="Energy & Utilities">
                      <option value="energy">Energy & Power</option>
                      <option value="renewable">Renewable Energy & Clean Tech</option>
                      <option value="utilities">Utilities & Infrastructure</option>
                    </optgroup>
                    <optgroup label="Professional Services">
                      <option value="legal">Legal & Policy Research</option>
                      <option value="education">Education & Learning Sciences</option>
                      <option value="consulting">Management Consulting</option>
                    </optgroup>
                    <optgroup label="Sciences & Research">
                      <option value="social">Social Sciences & Psychology</option>
                      <option value="environmental">Environmental & Sustainability</option>
                      <option value="materials">Materials Science</option>
                    </optgroup>
                    <optgroup label="Consumer & Industry">
                      <option value="retail">Retail & E-Commerce</option>
                      <option value="agriculture">Agriculture & Food Systems</option>
                      <option value="hospitality">Hospitality & Tourism</option>
                    </optgroup>
                  </select>
                </div>

                {/* Mode Selection */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Evaluation Mode</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => { setUseConsensusMode(true); setUseFastMode(false); setUseProgressiveMode(false); }}
                      className={`p-3 rounded-lg border text-left text-sm transition-all ${useConsensusMode && !useFastMode ? 'border-violet-500 bg-violet-50 ring-1 ring-violet-500' : 'border-slate-200 hover:border-slate-300'}`}
                    >
                      <div className="font-medium text-slate-800">High Accuracy</div>
                      <div className="text-xs text-slate-500 mt-0.5">3 AI models, ~60s</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => { setUseFastMode(true); setUseConsensusMode(false); setUseProgressiveMode(false); }}
                      className={`p-3 rounded-lg border text-left text-sm transition-all ${useFastMode ? 'border-amber-500 bg-amber-50 ring-1 ring-amber-500' : 'border-slate-200 hover:border-slate-300'}`}
                    >
                      <div className="font-medium text-slate-800">Fast Mode</div>
                      <div className="text-xs text-slate-500 mt-0.5">1 AI model, ~15s</div>
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </details>

          {/* Evaluate Button */}
          <Button
            onClick={handleEvaluate}
            disabled={!title.trim() || !content.trim() || content.length < 100}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white h-12 text-base font-medium shadow-md"
            size="lg"
            data-testid="evaluate-btn"
          >
            {useFastMode ? (
              <><Zap className="w-5 h-5 mr-2" /> Evaluate (~15s)</>
            ) : (
              <><Trophy className="w-5 h-5 mr-2" /> Evaluate Paper</>
            )}
          </Button>

          {content.length < 100 && content.length > 0 && (
            <p className="text-sm text-amber-600 text-center">
              Please provide at least 100 characters for accurate analysis
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickEvaluate;
